import React from 'react'
import dynamic from 'next/dynamic'

import { writeFile, readFile } from '@utils/fs-helpers'

import {
  getEntity,
  getHeaderData,
  getFooterData,
  getPostData,
  getPreviewByID,
} from '@api/index'

import { default as normalizeHeaderData } from '@models/Header'
import { default as normalizeFooterData } from '@models/Footer'
import { default as normalizeBreadcrumb } from '@models/Breadcrumb'
import { default as normalizePostData } from '@models/Post'
import normalizeMetadata from '@models/Metadata'

import { Col, Container, Row } from '@/atoms/Grid'

import Layout from '@/organisms/Layout'
import { Banner, PostContent, SectionBuilder } from '@/organisms'
import Breadcrumb from '@/molecules/Breadcrumb'
import PreviewBadge from '@/molecules/PreviewBadge'

const PostSidebar = dynamic(() => import('@/organisms/PostSidebar'), {
  ssr: false,
  loading: () => <p>Caricamento...</p>,
})

export const getStaticPaths = async () => {
  const posts = await Promise.all(
    [{ entity: 'blog' }].map(({ entity }) => getEntity({ entity }))
  )

  const paths = posts.reduce((acc, { data }) => {
    if (!data) return acc

    const pathData = Array.isArray(data) ? data : [data]
    const paths = pathData.map(({ slug }) => {
      return {
        params: {
          slug,
        },
      }
    })

    return [...acc, ...paths]
  }, [])

  try {
    const postsCache = posts.reduce((acc, { data, entity }) => {
      if (!data || data.length === 0) return acc

      const pathData = Array.isArray(data) ? data : [data]
      const paths = pathData.map(({ id, slug }) => {
        return {
          id,
          slug,
          entity,
        }
      })

      return [...acc, ...paths]
    }, [])

    await writeFile(
      'cache/static-paths-blog.json',
      JSON.stringify([...postsCache]),
      'utf-8'
    )
  } catch (error) {
    console.error('Generate cache file error', error)
  }

  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps = async ({ params, draftMode }) => {
  const getData = async (paramSlug) => {
    const cacheFile = await readFile('cache/static-paths-blog.json', 'utf-8')
    const staticPaths = JSON.parse(cacheFile)

    const staticPathData = staticPaths.find((path) => {
      const slug = Array.isArray(path.slug) ? path.slug.join('/') : path.slug
      return slug === `${paramSlug}`
    })

    if (staticPathData) {
      const { id } = staticPathData

      const [data] = await Promise.all([getPostData(id)])

      return data
    }
  }

  const postData = !draftMode
    ? await getData(params.slug)
    : await getPreviewByID(params.slug, 'previewBlogPost')

  const [footer, header] = await Promise.all([getFooterData(), getHeaderData()])

  if (!postData) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      isPreview: !!draftMode,
      metadata: normalizeMetadata(postData.seo),
      breadcrumb: normalizeBreadcrumb([
        {
          label: 'Blog',
          href: '/blog',
        },
        {
          label: postData.category.title,
        },
        {
          label: postData.title,
        },
      ]),
      footer: normalizeFooterData(footer),
      header: normalizeHeaderData(header),
      content: normalizePostData(postData),
    },
  }
}

const BlogPost = ({
  isPreview,
  metadata,
  breadcrumb,
  header,
  footer,
  content,
}) => {
  return (
    <Layout
      metadata={metadata}
      headerData={header}
      footerData={footer}
      colorScheme='white'
    >
      {isPreview ? <PreviewBadge /> : null}
      <Breadcrumb items={breadcrumb} />
      <Banner {...content.banner} />
      <Container>
        <Row>
          <Col
            xs={12}
            lg={3}
            order={{ xs: 2, lg: 1 }}
          >
            <PostSidebar sections={content.sidebar} />
          </Col>
          <Col
            xs={12}
            lg={7}
            $offset={{ xs: 0, lg: 2 }}
            order={{ xs: 1, lg: 2 }}
          >
            <PostContent {...content.content} />
          </Col>
        </Row>
      </Container>
      <SectionBuilder data={content.sections} />
    </Layout>
  )
}

export default BlogPost
